import React, { useEffect, useState } from 'react';
import { Card, Button, Pagination } from 'flowbite-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { getEmploymentStatusLabel } from '../utils/translations';
import { User } from '../types/user';
import { PaginatedResponse } from '../types/pagination';
import { toast } from 'react-toastify';

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const [emailFilter, setEmailFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [debouncedEmail, setDebouncedEmail] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmail(emailFilter);
    }, 500);

    return () => clearTimeout(timer);
  }, [emailFilter]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await api.users.getAll(
          currentPage, 
          ITEMS_PER_PAGE, 
          {
            email: debouncedEmail,
            gender: genderFilter === 'all' ? undefined : genderFilter
          }
        ) as PaginatedResponse<User>;
        setUsers(data.users);
        setTotalPages(data.pagination.total);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchUsers();
  }, [currentPage, debouncedEmail, genderFilter]);

  const handleViewDetails = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleExportAll = async () => {
    try {
      setLoading(true);
      await api.users.exportAll();
      toast.success('Users data exported successfully');
    } catch (error) {
      console.error('Error exporting users:', error);
      toast.error('Failed to export users data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportUser = async (userId: string) => {
    try {
      setLoading(true);
      await api.users.exportById(userId);
      toast.success('User data exported successfully');
    } catch (error) {
      console.error('Error exporting user:', error);
      toast.error('Failed to export user data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Card className="shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h5 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Users Directory
          </h5>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              Total Users: {totalPages}
            </span>
            <Button
              size="sm"
              onClick={handleExportAll}
              disabled={loading}
              color="blue"
            >
              {loading ? 'Exporting...' : 'Export All'}
            </Button>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by email..."
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Genders</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        
        <div className="flow-root">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {users.map((user) => (
              <li 
                key={user.id} 
                className="py-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 ease-in-out"
              >
                <div className="flex items-center space-x-4 px-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                      <span className="text-white text-lg font-semibold">
                        {user.first_name[0]}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {user.first_name} 
                    </p>
                    <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap gap-y-1 sm:gap-x-4">
                      <p className="flex items-center text-sm text-gray-500">
                        <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {user.email}
                      </p>
                      <p className="flex items-center text-sm text-gray-500">
                        <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {user.mobile_number}
                      </p>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {user.age} years
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {user.gender}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {getEmploymentStatusLabel(user.employment_status, true)}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {(user.average_monthly_income)}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <span className="text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </span>
                    <Button
                      size="sm"
                      gradientDuoTone="purpleToBlue"
                      outline
                      onClick={() => handleViewDetails(user.user_id)}
                      className="transition-all duration-200 hover:scale-105"
                    >
                      View Details
                    </Button>
                    <Button
                      size="sm"
                      gradientDuoTone="greenToBlue"
                      outline
                      onClick={() => handleExportUser(user.user_id)}
                      className="transition-all duration-200 hover:scale-105"
                    >
                      Export
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-center mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            showIcons
            className="inline-flex mt-2 gap-2"
          />
        </div>
      </Card>
    </div>
  );
};

export default Users; 